/*
  PDP-04: status line below the price ("Ab Lager, Versand heute").

  What this script does:
  - It works out on which day an order placed NOW is shipped and delivered, and writes that into
    the status line. The line itself, all wording and the rules per product come from
    snippets/pdp-statuszeile.liquid - this file only calculates and fills in.
  - It always uses the clock in Zurich, no matter where the visitor is.
  - When the customer picks another variant, the line follows. So does the stock row below the
    green block ("Nur noch 3 an Lager"): it is shown or hidden, and its number and colour are
    set (the "level" of the variant, worked out by the snippet).
  - While the stock row is shown, the green block does not repeat "Ab Lager": its first row is
    then "Versand heute" instead of "Ab Lager, Versand heute".
  - If the days cannot be calculated, only "Ab Lager" stays (and the stock row), the same as
    without JavaScript.

  Date rules:
  - Shipping Monday to Friday. An order before the cut-off leaves the same day, otherwise on the
    next working day.
  - Delivery takes as many working days after shipping as set in the theme settings.
  - Holidays from the theme settings count neither as shipping days nor as delivery days.
  - Wording: today, tomorrow, the day after tomorrow, otherwise the weekday; from 7 days away
    the date.

  Testing in the preview (never active in the live shop, see readTestMode): append to the URL
    ?statuszeile_test=2026-12-24T13:00     date and time in Zurich
    ?statuszeile_test=2026-12-24T12:00Z    a real instant in UTC, converted to Zurich time
*/
(function () {
  'use strict';

  // Snippet rendered twice on one page? Then the script is already there and only sets up the
  // new line.
  if (window.pdpStatusLine) {
    window.pdpStatusLine.start();
    return;
  }

  var DAY_MS = 24 * 60 * 60 * 1000;
  var MINUTE_MS = 60 * 1000;
  var SHORT_DAYS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];   // test note only
  var LEVELS = { low: true, medium: true };                      // levels the snippet has colours for


  /* ---------- Part 1: calculating (does not need the page) ---------- */

  // A calendar day as a running number. Counting days this way is not affected by time zones
  // or daylight saving time.
  function dayNumber(year, month, day) {
    return Math.round(Date.UTC(year, month - 1, day) / DAY_MS);
  }

  function dayParts(number) {
    var d = new Date(number * DAY_MS);
    return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate(), weekday: d.getUTCDay() };
  }

  // Returns the day number, or null if the date does not exist (e.g. 31.02.).
  function validDay(year, month, day) {
    if (year < 100) { year += 2000; }
    var number = dayNumber(year, month, day);
    var parts = dayParts(number);
    return (parts.year === year && parts.month === month && parts.day === day) ? number : null;
  }

  function twoDigits(number) {
    return (number < 10 ? '0' : '') + number;
  }

  function dayKey(number) {
    var parts = dayParts(number);
    return parts.year + '-' + twoDigits(parts.month) + '-' + twoDigits(parts.day);
  }

  // Reads a number from the settings and keeps it within sensible limits.
  function clamp(value, minimum, maximum, fallback) {
    var number = parseInt(value, 10);
    if (isNaN(number)) { return fallback; }
    return Math.min(maximum, Math.max(minimum, number));
  }

  // "12:00" -> 720 minutes since midnight. Anything unreadable counts as 12:00.
  function parseTime(text) {
    var match = /^\s*(\d{1,2})[:.](\d{2})\s*$/.exec(String(text || ''));
    if (!match || +match[1] > 23 || +match[2] > 59) { return 12 * 60; }
    return +match[1] * 60 + +match[2];
  }

  // Holiday list from the theme settings: one date per line (25.12.2026), a name may follow.
  // Two dates on one line (24.12.2026 - 02.01.2027) are a period, e.g. company holidays.
  // Lines without a readable date are skipped and remembered, so the test mode can show them.
  function parseHolidays(text) {
    var result = { days: {}, count: 0, unreadable: [] };
    var rows = String(text || '').split(/\r?\n/);
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i].replace(/^\s+|\s+$/g, '');
      if (!row) { continue; }

      var pattern = /(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4}|\d{2})(?!\d)|(\d{4})-(\d{2})-(\d{2})/g;
      var found = [];
      var match;
      while ((match = pattern.exec(row)) && found.length < 2) {
        found.push(match[4]
          ? validDay(+match[4], +match[5], +match[6])
          : validDay(+match[3], +match[2], +match[1]));
      }

      var from = found.length ? found[0] : null;
      var to = found.length > 1 ? found[1] : from;
      if (from === null || to === null || to < from || to - from > 366) {
        result.unreadable.push(row);
        continue;
      }
      for (var day = from; day <= to; day++) {
        if (!result.days[dayKey(day)]) {
          result.days[dayKey(day)] = true;
          result.count++;
        }
      }
    }
    return result;
  }

  // Date and time in Zurich for an instant - independent of the clock and the time zone of the
  // device. The browser knows the daylight saving rules itself.
  var zurichFormat = null;
  function zurichTime(instant) {
    if (!zurichFormat) {
      zurichFormat = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Zurich', hourCycle: 'h23',
        year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric'
      });
    }
    var parts = zurichFormat.formatToParts(instant);
    var value = {};
    for (var i = 0; i < parts.length; i++) {
      value[parts[i].type] = parseInt(parts[i].value, 10);
    }
    var now = { year: value.year, month: value.month, day: value.day, hour: value.hour % 24, minute: value.minute };
    if (isNaN(now.year + now.month + now.day + now.hour + now.minute)) {
      throw new Error('Zurich time not readable');
    }
    return now;
  }

  function isWorkingDay(number, holidays) {
    var weekday = dayParts(number).weekday;
    return weekday >= 1 && weekday <= 5 && !holidays[dayKey(number)];
  }

  function nextWorkingDay(number, holidays) {
    for (var i = 0; i < 400; i++) {
      number++;
      if (isWorkingDay(number, holidays)) { return number; }
    }
    throw new Error('No working day found');
  }

  // The heart of it: shipping day and delivery day for an order placed now.
  //   now   = { year, month, day, hour, minute } in Zurich
  //   rules = { cutoff (minutes), deliveryDays, holidays }
  function calculate(now, rules) {
    var today = dayNumber(now.year, now.month, now.day);

    // Out today? Only on a working day and only before the cut-off. 12:00 sharp is too late.
    var shipping = today;
    if (!isWorkingDay(today, rules.holidays) || now.hour * 60 + now.minute >= rules.cutoff) {
      shipping = nextWorkingDay(today, rules.holidays);
    }

    var delivery = shipping;
    for (var i = 0; i < rules.deliveryDays; i++) {
      delivery = nextWorkingDay(delivery, rules.holidays);
    }

    return { today: today, shipping: shipping, delivery: delivery };
  }

  // Puts values into a text template: "[stock], [shipping]".
  function fill(template, values) {
    return String(template).replace(/\[(\w+)\]/g, function (placeholder, name) {
      return Object.prototype.hasOwnProperty.call(values, name) ? values[name] : placeholder;
    });
  }

  // "heute", "morgen", "uebermorgen", "am Montag" or "am 14.10.". A weekday is only unambiguous
  // up to 6 days ahead, after that the date is used.
  function dayWord(target, today, texts) {
    var distance = target - today;
    var parts = dayParts(target);
    if (distance <= 0) { return texts.today; }
    if (distance === 1) { return texts.tomorrow; }
    if (distance === 2) { return texts.day_after_tomorrow; }
    if (distance <= 6) { return fill(texts.on_day, { day: texts.weekdays[parts.weekday] }); }
    return fill(texts.on_day, { day: parts.day + '.' + parts.month + '.' });
  }

  // Text of the stock row, e.g. "Nur noch 3 an Lager". Empty if there is no number to show.
  function stockText(count, texts) {
    return count > 0 ? fill(texts.low_stock, { count: count }) : '';
  }

  // Keeps a phrase on one line. On narrow phones a row may then only break between its two
  // parts ("Ab Lager," / "Versand am Montag"), never inside the second part.
  function keepTogether(text) {
    return String(text).replace(/ /g, '\u00a0');
  }

  function capitalize(text) {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  // Both rows of the green block for a point in time. They never contain a stock number.
  //   row 1: "Ab Lager" and shipping day   "Ab Lager, Versand heute"
  //          while a stock row ("Nur noch 3 an Lager") is shown, the shipping day alone: "Versand heute"
  //   row 2: delivery day and carrier      "Morgen bei dir mit PostPac Priority"
  function lineTexts(now, rules, texts, withStockRow) {
    var days = calculate(now, rules);
    var shipping = keepTogether(fill(texts.shipping, { day: dayWord(days.shipping, days.today, texts) }));
    var delivery = fill(texts.line2, { day: dayWord(days.delivery, days.today, texts), carrier: keepTogether(texts.carrier) });
    return {
      line1: withStockRow ? capitalize(shipping) : fill(texts.line1, { stock: texts.in_stock, shipping: shipping }),
      line2: capitalize(delivery),
      days: days
    };
  }

  // If the days cannot be calculated: "Ab Lager" only, the same text as without JavaScript.
  function fallbackTexts(texts) {
    return { line1: texts.in_stock, line2: '', days: null };
  }

  // What the stock row shows for a variant: the number (0 = no stock row) and the level.
  function stockOf(variant) {
    if (!variant || !variant.show || !(variant.count > 0)) { return { count: 0, level: '' }; }
    return { count: variant.count, level: LEVELS[variant.level] ? variant.level : 'medium' };
  }


  /* ---------- Part 2: the line on the page ---------- */

  var lines = [];   // all status lines on the page, normally exactly one

  function setHidden(element, hidden) {
    if (hidden) {
      element.setAttribute('hidden', '');
    } else {
      element.removeAttribute('hidden');
    }
  }

  // The line is invisible until the script is done (see the snippet). This releases it.
  function markReady(block) {
    if ((' ' + block.className + ' ').indexOf(' pdp-status--ready ') < 0) {
      block.className += ' pdp-status--ready';
    }
  }

  // Reads the test time from the URL. It only works if BOTH are true: Shopify rendered the page
  // from a theme that is not published ("test" from the snippet) AND the browser does not see
  // the live theme either. In the live shop the URL parameter is simply ignored.
  function readTestMode(data) {
    var theme = window.Shopify && window.Shopify.theme;
    if (data.test !== true || !theme || typeof theme.role !== 'string' || theme.role === 'main') {
      return null;
    }
    var timeParam = /[?&]statuszeile_test=([^&#]*)/.exec(String(window.location.search || ''));
    if (!timeParam) { return null; }

    var test = { time: null, unreadable: false };
    var t = /^(\d{4})-(\d{2})-(\d{2})[T_](\d{2})(?::|%3A)(\d{2})(Z?)$/i.exec(timeParam[1]);
    if (t && validDay(+t[1], +t[2], +t[3]) !== null && +t[4] < 24 && +t[5] < 60) {
      test.time = t[6]
        ? zurichTime(new Date(Date.UTC(+t[1], +t[2] - 1, +t[3], +t[4], +t[5])))
        : { year: +t[1], month: +t[2], day: +t[3], hour: +t[4], minute: +t[5] };
    } else {
      test.unreadable = true;
    }
    return test;
  }

  function shortDay(number) {
    var parts = dayParts(number);
    return SHORT_DAYS[parts.weekday] + ' ' + parts.day + '.' + parts.month + '.' + parts.year;
  }

  // Red box below the line, test mode only: shows what the calculation was based on.
  // The wording is German because it is read in the shop preview.
  function renderTestNote(line, now, text) {
    if (!line.test) { return; }
    if (!line.testNote) {
      line.testNote = document.createElement('p');
      line.testNote.className = 'pdp-status__test';
      line.testNote.setAttribute('data-wg-notranslate', '');   // Weglot: do not translate or store
      line.textArea.appendChild(line.testNote);
    }
    var parts = ['Testmodus (nur in der Vorschau)'];
    if (line.test.unreadable) {
      parts.push('Testzeit nicht lesbar, so geht es: ?statuszeile_test=2026-12-24T13:00');
    }
    if (now) {
      parts.push((line.test.time ? 'Testzeit ' : 'echte Zeit ') + shortDay(dayNumber(now.year, now.month, now.day)) +
        ' ' + twoDigits(now.hour) + ':' + twoDigits(now.minute) + ' in Z\u00fcrich');
    }
    if (text.days) {
      parts.push('Versand ' + shortDay(text.days.shipping));
      parts.push('Zustellung ' + shortDay(text.days.delivery));
    }
    parts.push('Bestellschluss ' + twoDigits(Math.floor(line.rules.cutoff / 60)) + ':' + twoDigits(line.rules.cutoff % 60));
    parts.push('Zustellzeit ' + line.rules.deliveryDays + ' Werktag(e)');
    parts.push('Feiertage erkannt ' + line.holidays.count);
    if (line.holidays.unreadable.length) {
      parts.push('nicht lesbar: ' + line.holidays.unreadable.join(' | '));
    }
    var note = parts.join(' \u00b7 ');
    if (note !== line.testNoteText) {
      line.testNoteText = note;
      line.testNote.textContent = note;
    }
  }

  // Which variant is selected right now? The theme keeps a hidden select up to date for that.
  function selectedVariant(line) {
    var select = document.getElementById(line.data.select);
    if (!select) { return line.variant; }
    if (select.disabled) { return ''; }   // this combination does not exist
    return String(select.value || line.variant);
  }

  // The stock row below the green block: shown only with a number, coloured by its level.
  // Its elements are looked up when needed and not kept: a translation tool may have replaced
  // them, and a page that still comes from the cache may not have the row at all.
  // The text is written only if it differs from what stands there (the snippet's text at first,
  // later what this script wrote). So a row that was hidden and comes back with the same
  // number keeps its translation on an English or French page. As a safeguard it is written
  // again if the row does not show the right number any more.
  function renderStock(line, stock) {
    var row = line.block.querySelector('[data-pdp-status-stock]');
    var label = line.block.querySelector('[data-pdp-status-stock-text]');
    if (!row) { return; }
    if (!label || !(stock.count > 0)) {
      setHidden(row, true);   // also if the text element is gone: better no row than a stale number
      return;
    }
    var text = stockText(stock.count, line.data.texts);
    if (text !== line.stockWritten || String(label.textContent).indexOf(String(stock.count)) < 0) {
      line.stockWritten = text;
      label.textContent = text;
    }
    row.setAttribute('data-level', stock.level);
    setHidden(row, false);
  }

  // Is the stock row on the page and shown right now? Asked of the page and not of the variant
  // list: on a page that still comes from the cache there is no stock row, and "Ab Lager" must
  // not go missing there.
  function stockRowShown(line) {
    var row = line.block.querySelector('[data-pdp-status-stock]');
    return !!row && !row.hasAttribute('hidden');
  }

  function render(line) {
    var variant = line.data.variants[line.variant];
    var now = null;
    var text;

    // The stock row comes first, because the first row of the green block depends on it. It has
    // its own "has anything changed?", so the check every minute does not touch it.
    var stock = stockOf(variant);
    var stockSignature = stock.count + '|' + stock.level;
    if (stockSignature !== line.lastStock) {
      line.lastStock = stockSignature;
      renderStock(line, stock);
    }

    if (!variant || !variant.show) {
      // Sold out, preorder etc.: no status line. The theme's old hint then applies again as
      // before (see the snippet).
      text = { line1: '', line2: '', hidden: true, days: null };
    } else {
      try {
        now = (line.test && line.test.time) || zurichTime(new Date());
        text = lineTexts(now, line.rules, line.data.texts, stockRowShown(line));
      } catch (error) {
        text = fallbackTexts(line.data.texts);
      }
      text.hidden = false;
    }

    // Only write when something has changed. Otherwise the check every minute would set the
    // text again and again - and Weglot (translation) would have to translate it each time.
    var signature = [text.hidden, text.line1, text.line2].join('|');
    if (signature !== line.last) {
      line.last = signature;
      if (!text.hidden) {
        line.line1.textContent = text.line1;
        line.line2.textContent = text.line2;
        setHidden(line.line2, !text.line2);
      }
      setHidden(line.block, text.hidden);
    }
    renderTestNote(line, now, text);
    markReady(line.block);
  }

  function renderAll(rereadVariant) {
    for (var i = lines.length - 1; i >= 0; i--) {
      var line = lines[i];
      if (!document.documentElement.contains(line.block)) {
        lines.splice(i, 1);   // the line is gone (the theme editor replaced the section)
        continue;
      }
      if (rereadVariant) { line.variant = selectedVariant(line); }
      render(line);
    }
  }

  // Look only after the theme has done its own work (updated the hidden select).
  function afterChange() {
    window.setTimeout(function () { renderAll(true); }, 0);
  }

  // The theme does not announce a variant change. But it updates the hidden select as soon as
  // the customer picks an option - that is what we listen for. Via jQuery we also get the
  // changes the theme triggers itself.
  function listen(area) {
    if (!area || area.pdpStatusListening) { return; }
    area.pdpStatusListening = true;
    if (window.jQuery) {
      window.jQuery(area).on('change', afterChange);
    } else {
      area.addEventListener('change', afterChange, false);
    }
  }

  function setUp(block) {
    if (block.pdpStatusDone) { return; }
    block.pdpStatusDone = true;

    var dataElement = block.querySelector('[data-pdp-status-data]');
    var data = JSON.parse(dataElement.textContent);
    var holidays = parseHolidays(data.holidays);
    var line = {
      block: block,
      textArea: block.querySelector('[data-pdp-status-text]'),
      line1: block.querySelector('[data-pdp-status-line1]'),
      line2: block.querySelector('[data-pdp-status-line2]'),
      data: data,
      variant: String(data.current),
      holidays: holidays,
      rules: {
        cutoff: parseTime(data.cutoff),
        deliveryDays: clamp(data.delivery_days, 1, 10, 1),
        holidays: holidays.days
      },
      test: readTestMode(data),
      testNote: null,
      testNoteText: null,
      last: null,
      lastStock: null,
      stockWritten: ''
    };
    // The snippet has already rendered the stock row of the selected variant: nothing to write
    // for it until the variant changes.
    var first = stockOf(data.variants[line.variant]);
    line.lastStock = first.count + '|' + first.level;
    line.stockWritten = stockText(first.count, data.texts);

    lines.push(line);
    listen(block.parentNode);
    render(line);
  }

  function start() {
    var blocks = document.querySelectorAll('[data-pdp-status]');
    for (var i = 0; i < blocks.length; i++) {
      try {
        setUp(blocks[i]);
      } catch (error) {
        markReady(blocks[i]);   // show what the snippet rendered: the stock row (if any) and "Ab Lager"
      }
    }
  }

  window.pdpStatusLine = {
    start: start,
    calculate: calculate,
    lineTexts: lineTexts,
    parseHolidays: parseHolidays,
    parseTime: parseTime,
    zurichTime: zurichTime
  };

  start();
  document.addEventListener('DOMContentLoaded', start, false);

  // Theme editor: the section was rebuilt, the line is a new one.
  document.addEventListener('shopify:section:load', start, false);

  // The clock keeps running: if the page stays open past the cut-off or past midnight, the line
  // would otherwise be wrong.
  window.setInterval(function () { renderAll(false); }, MINUTE_MS);
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) { renderAll(false); }
  }, false);
  window.addEventListener('pageshow', function () { renderAll(false); }, false);

  // Back button: the theme selects the variant from the URL again.
  window.addEventListener('popstate', afterChange, false);
})();
